import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./WebsitePopup.css";

import {
    API_BASE_URL,
    getAppFileUrl
} from "../config/apiConfig";


function WebsitePopup() {

    const [popups, setPopups] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showPopup, setShowPopup] = useState(false);

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | LOAD ACTIVE POPUPS
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const fetchPopups = async () => {

            try {

                const response = await axios.get(
                    `${API_BASE_URL}/popup_active.php`
                );

                console.log(
                    "POPUP API RESPONSE:",
                    response.data
                );


                if (
                    response.data?.success &&
                    Array.isArray(response.data?.data)
                ) {

                    const activePopups =
                        response.data.data;

                    setPopups(activePopups);


                    if (activePopups.length > 0) {

                        setCurrentIndex(0);

                        setShowPopup(true);

                    }

                } else {

                    setPopups([]);

                    setShowPopup(false);

                }

            } catch (error) {

                console.error(
                    "POPUP API ERROR:",
                    error
                );

                setPopups([]);

                setShowPopup(false);

            }

        };


        fetchPopups();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | NO POPUP
    |--------------------------------------------------------------------------
    */

    if (
        !showPopup ||
        popups.length === 0
    ) {

        return null;

    }


    /*
    |--------------------------------------------------------------------------
    | CURRENT POPUP
    |--------------------------------------------------------------------------
    */

    const currentPopup =
        popups[currentIndex];


    if (!currentPopup) {

        return null;

    }


    /*
    |--------------------------------------------------------------------------
    | MAIN POPUP IMAGE
    |--------------------------------------------------------------------------
    |
    | Example database values supported:
    |
    | uploads/popup/test.jpg
    | /uploads/popup/test.jpg
    | api/uploads/popup/test.jpg
    | https://sansha.in/api/uploads/popup/test.jpg
    |
    |--------------------------------------------------------------------------
    */

    const imageUrl =
        getAppFileUrl(
            currentPopup.image
        );


    /*
    |--------------------------------------------------------------------------
    | POPUP TYPE
    |--------------------------------------------------------------------------
    */

    const popupType =
        String(
            currentPopup.popup_type || "none"
        )
            .trim()
            .toLowerCase();


    /*
    |--------------------------------------------------------------------------
    | POPUP TARGET
    |--------------------------------------------------------------------------
    |
    | Supports:
    |
    | popup_target
    | target_file
    | target_value
    |
    |--------------------------------------------------------------------------
    */

    const popupTarget =
        currentPopup.popup_target ||
        currentPopup.target_file ||
        currentPopup.target_value ||
        "";


    const cleanTarget =
        String(popupTarget).trim();


    /*
    |--------------------------------------------------------------------------
    | DOES POPUP HAVE ACTION?
    |--------------------------------------------------------------------------
    */

    const hasAction =
        popupType !== "none" &&
        popupType !== "" &&
        cleanTarget !== "";


    /*
    |--------------------------------------------------------------------------
    | HANDLE POPUP IMAGE CLICK
    |--------------------------------------------------------------------------
    */

    const handlePopupClick = () => {

        if (
            popupType === "none" ||
            popupType === "" ||
            !cleanTarget
        ) {

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | IMAGE
        |--------------------------------------------------------------------------
        */

        if (popupType === "image") {

            const url =
                getAppFileUrl(cleanTarget);


            if (url) {

                window.open(
                    url,
                    "_blank",
                    "noopener,noreferrer"
                );

            }

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | PDF
        |--------------------------------------------------------------------------
        */

        if (popupType === "pdf") {

            const url =
                getAppFileUrl(cleanTarget);


            if (url) {

                window.open(
                    url,
                    "_blank",
                    "noopener,noreferrer"
                );

            }

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | EXISTING REACT COMPONENT / PAGE
        |--------------------------------------------------------------------------
        |
        | Examples:
        |
        | gallery
        | /gallery
        | contact
        | /contact
        |
        |--------------------------------------------------------------------------
        */

        if (
            popupType === "component" ||
            popupType === "existing"
        ) {

            let route =
                cleanTarget;


            if (!route.startsWith("/")) {

                route =
                    "/" + route;

            }


            setShowPopup(false);

            navigate(route);

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | EXTERNAL URL
        |--------------------------------------------------------------------------
        */

        if (
            popupType === "external" ||
            popupType === "url"
        ) {

            let url =
                cleanTarget;


            if (
                !url.startsWith("http://") &&
                !url.startsWith("https://")
            ) {

                url =
                    "https://" + url;

            }


            window.open(
                url,
                "_blank",
                "noopener,noreferrer"
            );


            return;

        }


        /*
        |--------------------------------------------------------------------------
        | UNKNOWN POPUP TYPE
        |--------------------------------------------------------------------------
        */

        console.warn(
            "Unknown popup type:",
            popupType
        );

    };


    /*
    |--------------------------------------------------------------------------
    | NEXT POPUP
    |--------------------------------------------------------------------------
    */

    const nextPopup = () => {

        if (
            currentIndex <
            popups.length - 1
        ) {

            setCurrentIndex(
                (prevIndex) =>
                    prevIndex + 1
            );

        } else {

            setShowPopup(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | PREVIOUS POPUP
    |--------------------------------------------------------------------------
    */

    const previousPopup = () => {

        if (currentIndex > 0) {

            setCurrentIndex(
                (prevIndex) =>
                    prevIndex - 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | CLOSE BUTTON
    |--------------------------------------------------------------------------
    |
    | Current behaviour:
    |
    | Popup 1 -> X -> Popup 2
    | Popup 2 -> X -> Popup 3
    | Last popup -> X -> Close
    |
    |--------------------------------------------------------------------------
    */

    const closePopup = () => {

        if (
            currentIndex <
            popups.length - 1
        ) {

            setCurrentIndex(
                (prevIndex) =>
                    prevIndex + 1
            );

        } else {

            setShowPopup(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <div className="website-popup-overlay">

            <div className="website-popup">


                {/* ================================================= */}
                {/* CLOSE BUTTON */}
                {/* ================================================= */}

                <button
                    type="button"
                    className="website-popup-close"
                    onClick={closePopup}
                    aria-label={
                        currentIndex <
                        popups.length - 1
                            ? "Next popup"
                            : "Close popup"
                    }
                >
                    ×
                </button>


                {/* ================================================= */}
                {/* POPUP IMAGE */}
                {/* ================================================= */}

                <div
                    className={
                        hasAction
                            ? "popup-clickable"
                            : "popup-not-clickable"
                    }

                    onClick={
                        hasAction
                            ? handlePopupClick
                            : undefined
                    }

                    title={
                        hasAction
                            ? "Click to open"
                            : ""
                    }
                >

                    <img
                        src={imageUrl}
                        alt={
                            currentPopup.title ||
                            "Website Popup"
                        }
                        className="website-popup-image"

                        onError={(event) => {

                            console.error(
                                "Popup image failed to load:",
                                imageUrl
                            );

                            event.currentTarget.style.display =
                                "none";

                        }}
                    />

                </div>


                {/* ================================================= */}
                {/* COUNTER */}
                {/* ================================================= */}

                {popups.length > 1 && (

                    <div className="popup-counter">

                        {currentIndex + 1}

                        {" / "}

                        {popups.length}

                    </div>

                )}


                {/* ================================================= */}
                {/* PREVIOUS BUTTON */}
                {/* ================================================= */}

                {popups.length > 1 &&
                    currentIndex > 0 && (

                    <button
                        type="button"
                        className="popup-previous"
                        onClick={previousPopup}
                        aria-label="Previous popup"
                    >

                        <span className="popup-arrow">
                            ‹
                        </span>

                        <span>
                            Previous
                        </span>

                    </button>

                )}


                {/* ================================================= */}
                {/* NEXT BUTTON */}
                {/* ================================================= */}

                {popups.length > 1 && (

                    <button
                        type="button"
                        className="popup-next"
                        onClick={nextPopup}

                        aria-label={
                            currentIndex <
                            popups.length - 1
                                ? "Next popup"
                                : "Close popup"
                        }
                    >

                        <span>

                            {currentIndex <
                            popups.length - 1
                                ? "Next"
                                : "Close"}

                        </span>


                        {currentIndex <
                        popups.length - 1 && (

                            <span className="popup-arrow">
                                ›
                            </span>

                        )}

                    </button>

                )}


            </div>

        </div>

    );

}

export default WebsitePopup;